import ReactDOM from "react-dom/client";
import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import useAdmissionChallanPdf from "../../hooks/useAdmissionChallanPdf";
import FullScreenLoader from "../../components/FullScreenLoader";
import { useNavigate } from "react-router-dom";

function AssessmentResult({ centerId, classId, regNo }) {
  const token = localStorage.getItem("token");
  const { generatePDF, loading } = useAdmissionChallanPdf();

  const navigate = useNavigate();

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    trigger,
    reset,
    getValues,
  } = useForm();

  const [centerClassesAssessmentPlan, setCenterClassesAssessmentPlan] =
    useState([]);

  useEffect(() => {
    const fetchCenterClassesAssessmentPlanByCenterIdAndClassId = async () => {
      try {
        const response = await get(
          `AssessmentPlan/GetCenterClassesAssessmentPlanByCenterIdAndClassIdAsync?centerId=${centerId}&classId=${classId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const { centerClassesAssessmentPlan } = response;

        const initializedData = centerClassesAssessmentPlan.map(
          (centerItem) => ({
            ...centerItem,
            centerAssessmentPlanDetail:
              centerItem.centerAssessmentPlanDetail.map((detail) => ({
                ...detail,
                obtainedScore: detail.obtainedScore || "",
              })),
          })
        );

        setCenterClassesAssessmentPlan(initializedData);

        reset({ centerClassesAssessmentPlan: initializedData });
      } catch (error) {
        console.error("Error fetching centerClassesAssessmentPlan:", error);
      }
    };

    if (centerId > 0 && classId > 0) {
      fetchCenterClassesAssessmentPlanByCenterIdAndClassId();
    }
  }, [centerId, classId, reset]);

  const handlePrintAdmissionChallanAction = async () => {
    if (!regNo) {
      alert("Please enter registration number.");
      return;
    }

    generatePDF(regNo);
  };

  const saveAssessmentResult = async (data) => {
    let hasFailedCompulsory = false;

    const transformPayload = (data) => {
      const payload = {
        id: 0,
        createdBy: localStorage.getItem("userId"),
        updatedBy: localStorage.getItem("userId"),
        code: "",
        registrationNo: regNo,
        assessmentResultDetailDto: [],
      };

      data.centerClassesAssessmentPlan.forEach((plan) => {
        plan.centerAssessmentPlanDetail.forEach((detail) => {
          const passingScore =
            (detail.passingScorePercentage / 100) * detail.totalScore;
          const resultStatus =
            detail.obtainedScore >= passingScore ? "Pass" : "Fail";

          if (detail.isCompulsory && resultStatus === "Fail") {
            hasFailedCompulsory = true;
          }

          payload.assessmentResultDetailDto.push({
            assessmentResultId: 0,
            obtainedScore: detail.obtainedScore,
            totalScore: detail.totalScore,
            passingScorePercentage: detail.passingScorePercentage,
            resultStatus,
            assessmentTypeId: detail.assessmentTypeId,
            assessmentTypeName: detail.assessmentTypeName,
          });
        });
      });

      return payload;
    };

    const formData = transformPayload(data);

    if (hasFailedCompulsory) {
      alert(
        "One or more compulsory assessments require a passing score. Please review the obtained scores."
      );
      return;
    }

    try {
      const response = await post(
        "AssessmentType/AddUpdateAssessmentResultAsync",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success("Success!");
        reset();
        navigate("/admissions");
        handlePrintAdmissionChallanAction();
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-body">
          {/* <h2 className="fs-7">Assessment Result</h2> */}
          <form
            className="row g-3"
            onSubmit={handleSubmit(saveAssessmentResult)}
          >
            <div className="col-12">
              <div className="row g-3">
                <div className="col-md-12">
                  <table className="table table-bordered custom-table m-0">
                    <thead>
                      <tr>
                        {/* <th>Center</th>
                        <th>Class</th> */}
                        <th>Assessment Type</th>
                        <th>Obtained Score</th>
                        <th>Passing Score (%)</th>
                        <th>Total Score</th>
                        <th>Compulsory</th>
                        <th>Result Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!centerClassesAssessmentPlan.length ? (
                        <tr className="text-center w-100">
                          <td colSpan="6">There are no records to display</td>
                        </tr>
                      ) : (
                        centerClassesAssessmentPlan.map(
                          (centerItem, centerIndex) =>
                            centerItem.centerAssessmentPlanDetail.map(
                              (item, detailIndex) => {
                                const passingScore =
                                  (item.passingScorePercentage / 100) *
                                  item.totalScore;

                                return (
                                  <tr key={item.id}>
                                    <td>{item.assessmentTypeName}</td>
                                    <td>
                                      <Controller
                                        name={`centerClassesAssessmentPlan[${centerIndex}].centerAssessmentPlanDetail[${detailIndex}].obtainedScore`}
                                        control={control}
                                        defaultValue={item.obtainedScore || ""}
                                        rules={{
                                          required:
                                            "Obtained score is required",
                                          min: {
                                            value: 0,
                                            message: "Score cannot be negative",
                                          },
                                          max: {
                                            value: item.totalScore,
                                            message: `Cannot exceed the total score (${item.totalScore})`,
                                          },
                                        }}
                                        render={({ field }) => (
                                          <input
                                            type="number"
                                            className="form-control"
                                            {...field}
                                            onChange={(e) => {
                                              const newObtainedScore = e.target
                                                .value
                                                ? parseFloat(e.target.value)
                                                : null;

                                              // Update the local state
                                              const updatedPlan = [
                                                ...centerClassesAssessmentPlan,
                                              ];
                                              updatedPlan[
                                                centerIndex
                                              ].centerAssessmentPlanDetail[
                                                detailIndex
                                              ].obtainedScore =
                                                newObtainedScore;

                                              setCenterClassesAssessmentPlan(
                                                updatedPlan
                                              );

                                              // Update field value and trigger validation
                                              field.onChange(newObtainedScore);
                                            }}
                                          />
                                        )}
                                      />
                                      {errors.centerClassesAssessmentPlan?.[
                                        centerIndex
                                      ]?.centerAssessmentPlanDetail?.[
                                        detailIndex
                                      ]?.obtainedScore && (
                                        <div className="text-danger">
                                          {
                                            errors.centerClassesAssessmentPlan[
                                              centerIndex
                                            ].centerAssessmentPlanDetail[
                                              detailIndex
                                            ].obtainedScore.message
                                          }
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        className="form-control"
                                        defaultValue={
                                          item.passingScorePercentage
                                        }
                                        disabled={true}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        className="form-control"
                                        defaultValue={item.totalScore}
                                        disabled={true}
                                      />
                                    </td>
                                    <td className="text-center">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        defaultChecked={item.isCompulsory}
                                        disabled={true}
                                      />
                                    </td>
                                    <td>
                                      <div
                                        className={
                                          item.obtainedScore === null
                                            ? "text-muted"
                                            : item.obtainedScore >= passingScore
                                            ? "text-success"
                                            : "text-danger"
                                        }
                                      >
                                        {item.obtainedScore === null
                                          ? ""
                                          : item.obtainedScore >= passingScore
                                          ? "Pass"
                                          : "Fail"}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              }
                            )
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-12">
              <button
                className="btn btn-primary btn-sm"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submit" : "Submit"}
              </button>
            </div>
          </form>

          {/* Conditionally render PdfGenerator after submission */}
          {/* {isPdfGenerated && <PdfGenerator content={htmlContent} />} */}
        </div>
      </div>
      {loading && <FullScreenLoader />}
    </>
  );
}

export default AssessmentResult;
